
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/20 via-accent/10 to-secondary">
      <div className="container max-w-4xl mx-auto py-12 px-4">
        <div className="mb-8">
          <Button 
            variant="ghost" 
            className="flex items-center gap-2" 
            asChild
          >
            <Link to="/">
              <ChevronLeft className="h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
        
        <div className="bg-white/80 backdrop-blur-sm p-8 rounded-xl shadow-lg">
          <h1 className="text-3xl font-bold text-primary mb-6">Privacy Policy</h1>
          
          <div className="prose prose-blue max-w-none">
            <p className="mb-4">Last updated: {new Date().toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}</p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">1. Introduction</h2>
            <p>
              Shared Matter ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI assistant application ("Shared Matter"). Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the application.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">2. Collection of Information</h2>
            <p>
              We collect several types of information from and about users of our application, including:
            </p>
            <ul className="list-disc pl-6 my-4 space-y-2">
              <li>Personal information (such as name and email address) that you voluntarily provide when registering or using our services.</li>
              <li>Information about your interactions with Shared Matter, including conversations, preferences, and files you upload.</li>
              <li>Technical data, such as your IP address, browser type, and device information.</li>
              <li>Information from third-party services you connect to Shared Matter, such as calendar or communication platforms.</li>
            </ul>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">3. Use of Information</h2>
            <p>
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 my-4 space-y-2">
              <li>Provide, operate, and maintain our services.</li>
              <li>Improve and personalize your experience with Shared Matter.</li>
              <li>Develop new products, services, features, and functionality.</li>
              <li>Process transactions and send related information.</li>
              <li>Respond to comments, questions, and requests.</li>
              <li>Send you technical notices, updates, security alerts, and support messages.</li>
              <li>Protect, investigate, and deter against fraudulent, unauthorized, or illegal activity.</li>
            </ul>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">4. Data Storage and Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect the personal information that we collect and process about you. The measures we use are designed to provide a level of security appropriate to the risk of processing your personal information.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">5. AI and Machine Learning Data Use</h2>
            <p>
              Shared Matter does not retain user data obtained through Workspace APIs to develop, improve, or train generalized AI and/or ML models.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">6. Data Sharing and Disclosure</h2>
            <p>
              We may share your information in the following situations:
            </p>
            <ul className="list-disc pl-6 my-4 space-y-2">
              <li>With service providers who perform services for us.</li>
              <li>To comply with applicable laws and regulations.</li>
              <li>To protect and defend our rights and property.</li>
              <li>With your consent or at your direction.</li>
            </ul>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">7. Your Rights</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 my-4 space-y-2">
              <li>Access to your personal information.</li>
              <li>Correction of inaccurate or incomplete information.</li>
              <li>Deletion of your personal information.</li>
              <li>Restriction of processing of your personal information.</li>
              <li>Data portability.</li>
            </ul>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">8. Changes to this Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this page.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">9. Contact Information</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="mt-2">
              K2 Innoventions<br />
              Email: privacy@k2innoventions.com
            </p>
          </div>
        </div>
      </div>
      
      <footer className="w-full p-4 text-center text-sm text-foreground/70 bg-secondary/50 backdrop-blur-sm">
        &copy; {new Date().getFullYear()} K2 Innoventions | All Rights Reserved
      </footer>
    </div>
  );
};

export default Privacy;
